import React, { useEffect, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { getUsersAsync } from "../../../slices/user";
import { createHseEvent, updateHseEvent } from "../../../api/hse/events";
import toast from "react-hot-toast";
import * as Yup from "yup";
import { Form, Formik } from "formik";
import { PMDatePicker } from "../../utils/PMDatePicker";
import { CustomerTag } from "../../utils/moduleTags/customerTag";
import { EmployeeTag } from "../../utils/moduleTags/employeeTag";
import { ProjectTag } from "../../utils/moduleTags/projectTag";
import { ProjectFolderTag } from "../../utils/moduleTags/projectFolderTag";
import { MultiEmployeeSelector } from "../../utils/moduleSelectors/multiEmployeeSelector";
import { DocumentManagerV6 } from "../Media/DocumentManagerV6";
import { EmployeePickerSheet, avatarColor, initialsOf } from "../hours/hourFormPickers";
import { StaticOptionPickerSheet, DepartmentPickerSheet } from "./hseEventPickers";

// ── Promang brand/design tokens (mirrors hour_form.js) ───────────────────────
const ACCENT = {
  solid: "#1463C7",
  grad: "linear-gradient(135deg,#1463C7,#2D8BE8)",
  deep: "#0B3A78",
  mid: "#2D8BE8",
  heroBg: "linear-gradient(160deg,#EAF3FD,#F2F8FE)",
  heroBorder: "#BFDCF7",
  soft: "#EAF3FD",
  glow: "rgba(20,99,199,",
};

const dsLabelStyle = { display: "block", fontSize: 13, fontWeight: 700, color: "#334155", margin: "0 0 9px" };
const dsFieldCardStyle = {
  width: "100%",
  display: "flex",
  alignItems: "center",
  gap: 12,
  padding: "14px 16px",
  border: "1.5px solid #E2E8F0",
  borderRadius: 14,
  background: "#fff",
  fontFamily: "inherit",
  textAlign: "left",
};
const dsInputStyle = {
  width: "100%",
  padding: "14px 16px",
  border: "1.5px solid #E2E8F0",
  borderRadius: 14,
  fontSize: 16,
  color: "#0F172A",
  background: "#fff",
  outline: "none",
  fontFamily: "inherit",
  boxSizing: "border-box",
};
const dsTextareaStyle = {
  ...dsInputStyle,
  minHeight: 96,
  resize: "vertical",
  lineHeight: 1.5,
};
const dsSectionTitleStyle = {
  fontSize: 15,
  fontWeight: 800,
  color: "#0F172A",
  margin: "4px 0 0",
  letterSpacing: "-.01em",
};
const canvasStyleFor = (wide) => ({
  background: "#F8FAFC",
  borderRadius: 16,
  padding: wide ? "12px 16px 16px" : 16,
  display: wide ? "grid" : "flex",
  flexDirection: wide ? undefined : "column",
  gridTemplateColumns: wide ? "1fr 1fr" : undefined,
  gap: wide ? "16px 20px" : 20,
  alignContent: "start",
});

const spanFull = (wide) => (wide ? { gridColumn: "1 / -1" } : {});

function useIsWide(breakpoint = 768) {
  const [wide, setWide] = useState(
    () => typeof window !== "undefined" && window.matchMedia(`(min-width: ${breakpoint}px)`).matches
  );
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const mq = window.matchMedia(`(min-width: ${breakpoint}px)`);
    const update = () => setWide(mq.matches);
    update();
    mq.addEventListener?.("change", update);
    return () => mq.removeEventListener?.("change", update);
  }, [breakpoint]);
  return wide;
}

const dsPillStyle = (active) =>
  active
    ? {
        padding: "9px 15px",
        borderRadius: 999,
        fontSize: 14,
        fontWeight: 700,
        cursor: "pointer",
        fontFamily: "inherit",
        border: "1.5px solid transparent",
        color: "#fff",
        background: ACCENT.grad,
        boxShadow: `0 6px 14px -5px ${ACCENT.glow}.6)`,
      }
    : {
        padding: "9px 15px",
        borderRadius: 999,
        fontSize: 14,
        fontWeight: 700,
        cursor: "pointer",
        fontFamily: "inherit",
        border: "1.5px solid #E2E8F0",
        background: "#fff",
        color: "#475569",
      };

const DsChevron = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#94A3B8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M6 9l6 6 6-6" />
  </svg>
);

const pad2 = (n) => String(n).padStart(2, "0");
const toISODate = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
const todayISODate = () => toISODate(new Date());
const yesterdayISODate = () => {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return toISODate(d);
};
const formatLongDateNb = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(`${String(dateStr).substring(0, 10)}T00:00:00`);
  if (Number.isNaN(d.getTime())) return String(dateStr);
  const s = d.toLocaleDateString("nb-NO", { weekday: "long", day: "numeric", month: "long" });
  return s.charAt(0).toUpperCase() + s.slice(1);
};

const optionLabel = (options, value, placeholder) => {
  if (!value) return placeholder;
  const match = options.find((o) => String(o.value) === String(value));
  return match?.title || placeholder;
};

const FieldError = ({ error, touched }) =>
  error && touched ? <p style={{ marginTop: 6, fontSize: 12, color: "#DC2626" }}>{error}</p> : null;

const DesignCheckbox = ({ checked, onChange, label }) => (
  <label
    style={{
      display: "flex",
      alignItems: "center",
      gap: 12,
      padding: "14px 16px",
      border: "1.5px solid #E2E8F0",
      borderRadius: 14,
      background: checked ? ACCENT.soft : "#fff",
      cursor: "pointer",
      fontFamily: "inherit",
    }}
  >
    <input
      type="checkbox"
      checked={checked}
      onChange={onChange}
      style={{ width: 20, height: 20, accentColor: ACCENT.solid, flex: "0 0 auto" }}
    />
    <span style={{ fontSize: 15, fontWeight: 600, color: "#0F172A" }}>{label}</span>
  </label>
);

export const HseEventForm = ({ event, onSubmit, onCancel }) => {
  const { t } = useTranslation();
  const isWide = useIsWide();
  const dispatch = useDispatch();
  const itemState = useSelector((store) => store.users);
  const [loading, setLoading] = useState(false);
  const [showMultiEmployeeSelector, setShowMultiEmployeeSelector] = useState(false);
  const [involvedEmployees, setInvolvedEmployees] = useState([]);
  const [responsibleUser, setResponsibleUser] = useState(event?.responsible_user || null);
  const [selectedDepartment, setSelectedDepartment] = useState(event?.department || null);

  // Picker open state
  const [eventTypePickerOpen, setEventTypePickerOpen] = useState(false);
  const [severityPickerOpen, setSeverityPickerOpen] = useState(false);
  const [statusPickerOpen, setStatusPickerOpen] = useState(false);
  const [categoryPickerOpen, setCategoryPickerOpen] = useState(false);
  const [responsiblePickerOpen, setResponsiblePickerOpen] = useState(false);
  const [departmentPickerOpen, setDepartmentPickerOpen] = useState(false);

  const formatTimeForInput = (timeValue) => {
    if (!timeValue) return "";
    if (typeof timeValue === "string" && timeValue.match(/^\d{1,2}:\d{2}$/)) {
      return timeValue;
    }
    try {
      const time = new Date(timeValue);
      if (isNaN(time.getTime())) return "";
      return `${time.getHours().toString().padStart(2, "0")}:${time.getMinutes().toString().padStart(2, "0")}`;
    } catch {
      return "";
    }
  };

  useEffect(() => {
    if (event?.involved_employees) {
      if (Array.isArray(event.involved_employees)) {
        setInvolvedEmployees(event.involved_employees);
      } else if (typeof event.involved_employees === "string") {
        try {
          const userIds = event.involved_employees.split(", ").map((id) => parseInt(id.trim()));
          const users = itemState.data?.filter((user) => userIds.includes(user.id)) || [];
          setInvolvedEmployees(users);
        } catch {
          setInvolvedEmployees([]);
        }
      }
    }
  }, [event?.involved_employees, itemState.data]);

  useEffect(() => {
    if (!itemState.data || itemState.data.length === 0) {
      dispatch(getUsersAsync({ filters: [{ name: "EMPLOYEE", value: true }] }));
    }
  }, []);

  const eventTypes = useMemo(
    () => [
      { value: "injury", title: t("Skade") },
      { value: "near_miss", title: t("Nærhendelse") },
      { value: "property_damage", title: t("Materiellskade") },
      { value: "environmental", title: t("Miljøhendelse") },
      { value: "other", title: t("Annet") },
    ],
    [t]
  );

  const severityLevels = useMemo(
    () => [
      { value: "low", title: t("Lav") },
      { value: "medium", title: t("Medium") },
      { value: "high", title: t("Høy") },
      { value: "critical", title: t("Kritisk") },
    ],
    [t]
  );

  const eventCategories = useMemo(
    () => [
      { value: "law_violation", title: t("Avvik lov") },
      { value: "regulation_violation", title: t("Avvik forskrift") },
      { value: "procedure_violation", title: t("Brudd på prosedyre") },
      { value: "training_violation", title: t("Brudd på opplæring") },
      { value: "information", title: t("Informasjon") },
      { value: "other", title: t("Annet") },
    ],
    [t]
  );

  const statusOptions = useMemo(
    () => [
      { value: "draft", title: t("Kladd") },
      { value: "submitted", title: t("Innsendt") },
      { value: "under_review", title: t("Under vurdering") },
      { value: "closed", title: t("Lukket") },
    ],
    [t]
  );

  const handleInvolvedEmployeesSelected = (employees) => {
    setInvolvedEmployees(employees);
  };

  const handleSubmit = async (values, helpers) => {
    try {
      setLoading(true);
      const submitData = {
        ...values,
        involved_employees: involvedEmployees.map((emp) => emp.id),
      };
      let response;
      if (event) {
        response = await updateHseEvent({ ...submitData, id: event.id });
        toast.success(t("Hendelse ble oppdatert"));
      } else {
        response = await createHseEvent(submitData);
        toast.success(t("Hendelse ble opprettet"));
      }
      onSubmit(response.data);
      helpers.setStatus({ success: true });
      helpers.setSubmitting(false);
    } catch (err) {
      console.error("Error saving HSE event:", err);
      toast.error(t("Kunne ikke lagre hendelse"));
      helpers.setStatus({ success: false });
      helpers.setErrors({ submit: err.message });
      helpers.setSubmitting(false);
    } finally {
      setLoading(false);
    }
  };

  const initialValues = {
    title: event?.title || "",
    description: event?.description || "",
    event_date: event?.event_date || new Date().toISOString().slice(0, 10),
    event_time: formatTimeForInput(event?.event_time),
    event_type: event?.event_type || "",
    severity_level: event?.severity_level || "",
    location: event?.location || "",
    department_id: event?.department_id || "",
    building: event?.building || "",
    room_area: event?.room_area || "",
    location_details: event?.location_details || "",
    involved_persons: event?.involved_persons || "",
    witnesses: event?.witnesses || "",
    other_persons_involved: event?.other_persons_involved || "",
    responsible_user_id: event?.responsible_user_id ? parseInt(event.responsible_user_id) : "",
    event_category: event?.event_category || "",
    immediate_cause: event?.immediate_cause || "",
    underlying_cause: event?.underlying_cause || "",
    category_details: event?.category_details || "",
    root_cause_analysis_required: event?.root_cause_analysis_required || false,
    root_cause_analysis_details: event?.root_cause_analysis_details || "",
    immediate_actions_required: event?.immediate_actions_required || false,
    immediate_actions_details: event?.immediate_actions_details || "",
    suggested_actions: event?.suggested_actions || "",
    actions_created: event?.actions_created || false,
    action_notes: event?.action_notes || "",
    status: event?.status || "draft",
    project_id: event?.project_id || "",
    customer_id: event?.customer_id || "",
    folder_id: event?.folder_id || "",
    requires_investigation: event?.requires_investigation || false,
    requires_reporting_to_authorities: event?.requires_reporting_to_authorities || false,
    authority_report_details: event?.authority_report_details || "",
    estimated_cost: event?.estimated_cost || "",
    lessons_learned: event?.lessons_learned || "",
    documents: event?.documents || [],
  };

  const validationSchema = Yup.object({
    title: Yup.string().required(t("Tittel er påkrevd")),
    event_date: Yup.date().required(t("Dato er påkrevd")),
    event_type: Yup.string().required(t("Hendelsestype er påkrevd")),
    location: Yup.string().required(t("Sted er påkrevd")),
  });

  const renderPickerTrigger = (label, valueLabel, hasValue, onOpen, error, touched) => (
    <div>
      <label style={dsLabelStyle}>{label}</label>
      <button type="button" onClick={onOpen} style={{ ...dsFieldCardStyle, cursor: "pointer" }}>
        <span style={{ flex: 1, fontSize: 16, fontWeight: 600, color: hasValue ? "#0F172A" : "#94A3B8" }}>{valueLabel}</span>
        <DsChevron />
      </button>
      <FieldError error={error} touched={touched} />
    </div>
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-contain -mt-1">
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {(formik) => {
          const responsibleName =
            responsibleUser?.displayTitle ||
            itemState.data?.find((u) => String(u.id) === String(formik.values.responsible_user_id))?.displayTitle ||
            t("Velg ansvarlig person");

          const departmentName =
            selectedDepartment?.displayTitle || t("Velg avdeling");

          const canSave =
            !!formik.values.title?.trim() &&
            !!formik.values.event_date &&
            !!formik.values.event_type &&
            !!formik.values.location?.trim() &&
            !loading &&
            !formik.isSubmitting;

          return (
            <Form>
              {onCancel && (
                <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 8 }}>
                  <button
                    type="button"
                    onClick={onCancel}
                    aria-label={t("Lukk")}
                    style={{
                      width: 40,
                      height: 40,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      border: "none",
                      background: "#F1F5F9",
                      borderRadius: 11,
                      cursor: "pointer",
                      color: "#475569",
                      flex: "0 0 auto",
                    }}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
                      <path d="M18 6L6 18M6 6l12 12" />
                    </svg>
                  </button>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 18, fontWeight: 800, color: "#0F172A", letterSpacing: "-.01em" }}>
                      {event ? t("Rediger hendelse") : t("Ny HMS-hendelse")}
                    </div>
                    <div
                      style={{
                        fontSize: 12.5,
                        color: "#94A3B8",
                        fontWeight: 600,
                        textTransform: "capitalize",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {formatLongDateNb(formik.values.event_date)}
                    </div>
                  </div>
                </div>
              )}

              <div style={canvasStyleFor(isWide)}>
                {/* ── Grunnleggende informasjon ── */}
                <div style={{ ...dsSectionTitleStyle, ...spanFull(isWide) }}>{t("Grunnleggende informasjon")}</div>

                <div style={spanFull(isWide)}>
                  <label style={dsLabelStyle}>{t("Tittel")}</label>
                  <input
                    type="text"
                    name="title"
                    value={formik.values.title}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    placeholder={t("Angi tittel for hendelsen")}
                    style={{
                      ...dsInputStyle,
                      borderColor: formik.errors.title && formik.touched.title ? "#FCA5A5" : "#E2E8F0",
                    }}
                  />
                  <FieldError error={formik.errors.title} touched={formik.touched.title} />
                </div>

                {/* Dato + tidspunkt — pills over begge, side om side på desktop */}
                <div style={spanFull(isWide)}>
                  <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
                    <button
                      type="button"
                      style={dsPillStyle(formik.values.event_date === todayISODate())}
                      onClick={() => formik.setFieldValue("event_date", todayISODate())}
                    >
                      {t("I dag")}
                    </button>
                    <button
                      type="button"
                      style={dsPillStyle(formik.values.event_date === yesterdayISODate())}
                      onClick={() => formik.setFieldValue("event_date", yesterdayISODate())}
                    >
                      {t("I går")}
                    </button>
                  </div>
                  <div
                    style={
                      isWide
                        ? { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 20px", alignItems: "start" }
                        : { display: "flex", flexDirection: "column", gap: 20 }
                    }
                  >
                    <div>
                      <label style={dsLabelStyle}>{t("Dato")}</label>
                      <PMDatePicker
                        triggerVariant="design"
                        accentColor={ACCENT.solid}
                        name="event_date"
                        value={formik.values.event_date}
                        onChange={(e) => formik.setFieldValue("event_date", e?.target?.value)}
                        placeholder={t("Velg dato")}
                        errors={formik.errors.event_date}
                        touched={formik.touched.event_date}
                      />
                    </div>
                    <div>
                      <label style={dsLabelStyle}>{t("Tidspunkt")}</label>
                      <div style={{ ...dsFieldCardStyle, padding: "14px 16px", minHeight: 52, boxSizing: "border-box" }}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={ACCENT.solid} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <circle cx="12" cy="12" r="9" />
                          <path d="M12 7v5l3 2" />
                        </svg>
                        <input
                          type="time"
                          name="event_time"
                          value={formik.values.event_time}
                          onChange={formik.handleChange}
                          style={{
                            flex: 1,
                            border: "none",
                            outline: "none",
                            fontSize: 16,
                            fontWeight: 600,
                            color: "#0F172A",
                            background: "transparent",
                            fontFamily: "inherit",
                            minHeight: 24,
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {renderPickerTrigger(
                  t("Hendelsestype"),
                  optionLabel(eventTypes, formik.values.event_type, t("Velg hendelsestype")),
                  !!formik.values.event_type,
                  () => setEventTypePickerOpen(true),
                  formik.errors.event_type,
                  formik.touched.event_type
                )}
                <StaticOptionPickerSheet
                  open={eventTypePickerOpen}
                  onClose={() => setEventTypePickerOpen(false)}
                  title={t("Velg hendelsestype")}
                  options={eventTypes}
                  selectedValue={formik.values.event_type}
                  onSelect={(opt) => formik.setFieldValue("event_type", opt.value)}
                  searchPlaceholder={t("Søk hendelsestype…")}
                />

                {renderPickerTrigger(
                  t("Alvorlighetsgrad"),
                  optionLabel(severityLevels, formik.values.severity_level, t("Velg alvorlighetsgrad")),
                  !!formik.values.severity_level,
                  () => setSeverityPickerOpen(true)
                )}
                <StaticOptionPickerSheet
                  open={severityPickerOpen}
                  onClose={() => setSeverityPickerOpen(false)}
                  title={t("Velg alvorlighetsgrad")}
                  options={severityLevels}
                  selectedValue={formik.values.severity_level}
                  onSelect={(opt) => formik.setFieldValue("severity_level", opt.value)}
                  searchPlaceholder={t("Søk…")}
                />

                {renderPickerTrigger(
                  t("Status"),
                  optionLabel(statusOptions, formik.values.status, t("Velg status")),
                  !!formik.values.status,
                  () => setStatusPickerOpen(true)
                )}
                <StaticOptionPickerSheet
                  open={statusPickerOpen}
                  onClose={() => setStatusPickerOpen(false)}
                  title={t("Velg status")}
                  options={statusOptions}
                  selectedValue={formik.values.status}
                  onSelect={(opt) => formik.setFieldValue("status", opt.value)}
                  searchPlaceholder={t("Søk…")}
                />

                <div style={{ ...spanFull(isWide) }}>
                  <label style={dsLabelStyle}>{t("Beskrivelse")}</label>
                  <textarea
                    rows={4}
                    name="description"
                    style={dsTextareaStyle}
                    placeholder={t("Beskriv hendelsen detaljert...")}
                    value={formik.values.description}
                    onChange={(e) => formik.setFieldValue("description", e.target.value)}
                  />
                </div>

                {/* ── Sted og avdeling ── */}
                <div style={{ ...dsSectionTitleStyle, marginTop: 8, ...spanFull(isWide) }}>{t("Sted og avdeling")}</div>

                <div>
                  <label style={dsLabelStyle}>{t("Sted")}</label>
                  <input
                    type="text"
                    name="location"
                    value={formik.values.location}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    style={{
                      ...dsInputStyle,
                      borderColor: formik.errors.location && formik.touched.location ? "#FCA5A5" : "#E2E8F0",
                    }}
                  />
                  <FieldError error={formik.errors.location} touched={formik.touched.location} />
                </div>

                <div>
                  <label style={dsLabelStyle}>{t("Avdeling")}</label>
                  <button
                    type="button"
                    onClick={() => setDepartmentPickerOpen(true)}
                    style={{ ...dsFieldCardStyle, cursor: "pointer" }}
                  >
                    <span style={{ flex: 1, fontSize: 16, fontWeight: 600, color: formik.values.department_id ? "#0F172A" : "#94A3B8" }}>
                      {formik.values.department_id ? departmentName : t("Velg avdeling")}
                    </span>
                    <DsChevron />
                  </button>
                  <DepartmentPickerSheet
                    open={departmentPickerOpen}
                    onClose={() => setDepartmentPickerOpen(false)}
                    selectedId={formik.values.department_id}
                    onSelect={(dept) => {
                      formik.setFieldValue("department_id", dept?.id ?? "");
                      setSelectedDepartment(dept);
                    }}
                  />
                </div>

                <div>
                  <label style={dsLabelStyle}>{t("Bygg/Område")}</label>
                  <input
                    type="text"
                    name="building"
                    value={formik.values.building}
                    onChange={formik.handleChange}
                    style={dsInputStyle}
                  />
                </div>

                <div>
                  <label style={dsLabelStyle}>{t("Rom/Område")}</label>
                  <input
                    type="text"
                    name="room_area"
                    value={formik.values.room_area}
                    onChange={formik.handleChange}
                    style={dsInputStyle}
                  />
                </div>

                <div style={spanFull(isWide)}>
                  <label style={dsLabelStyle}>{t("Detaljert beskrivelse av stedet")}</label>
                  <textarea
                    rows={3}
                    name="location_details"
                    style={{ ...dsTextareaStyle, minHeight: 72 }}
                    placeholder={t("Beskriv stedet mer detaljert...")}
                    value={formik.values.location_details}
                    onChange={(e) => formik.setFieldValue("location_details", e.target.value)}
                  />
                </div>

                {/* ── Involverte personer ── */}
                <div style={{ ...dsSectionTitleStyle, marginTop: 8, ...spanFull(isWide) }}>{t("Involverte personer")}</div>

                <div>
                  <label style={dsLabelStyle}>{t("Ansvarlig person")}</label>
                  <button
                    type="button"
                    onClick={() => setResponsiblePickerOpen(true)}
                    style={{ ...dsFieldCardStyle, cursor: "pointer" }}
                  >
                    <span
                      style={{
                        width: 40,
                        height: 40,
                        borderRadius: 11,
                        flex: "0 0 auto",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "#fff",
                        fontWeight: 800,
                        fontSize: 14,
                        background: avatarColor(responsibleUser?.id || formik.values.responsible_user_id || responsibleName),
                      }}
                    >
                      {initialsOf(responsibleName)}
                    </span>
                    <span style={{ flex: 1, minWidth: 0, fontSize: 16, fontWeight: 600, color: formik.values.responsible_user_id ? "#0F172A" : "#94A3B8" }}>
                      {formik.values.responsible_user_id ? responsibleName : t("Velg ansvarlig person")}
                    </span>
                    <DsChevron />
                  </button>
                  <EmployeePickerSheet
                    open={responsiblePickerOpen}
                    onClose={() => setResponsiblePickerOpen(false)}
                    selectedId={formik.values.responsible_user_id}
                    onSelect={(obj) => {
                      formik.setFieldValue("responsible_user_id", obj?.id ?? "");
                      setResponsibleUser(obj);
                    }}
                  />
                </div>

                <div>
                  <label style={dsLabelStyle}>{t("Involverte personer")}</label>
                  <button
                    type="button"
                    onClick={() => setShowMultiEmployeeSelector(true)}
                    style={{ ...dsFieldCardStyle, cursor: "pointer" }}
                  >
                    <span style={{ flex: 1, fontSize: 16, fontWeight: 600, color: involvedEmployees.length > 0 ? "#0F172A" : "#94A3B8" }}>
                      {involvedEmployees.length > 0
                        ? `${involvedEmployees.length} ${t("personer valgt")}`
                        : t("Velg involverte personer")}
                    </span>
                    <DsChevron />
                  </button>
                  {involvedEmployees.length > 0 && (
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 10 }}>
                      {involvedEmployees.map((employee) => (
                        <span
                          key={employee.id}
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 6,
                            padding: "6px 12px",
                            borderRadius: 999,
                            fontSize: 13,
                            fontWeight: 700,
                            background: ACCENT.soft,
                            color: ACCENT.solid,
                          }}
                        >
                          {employee.displayTitle}
                        </span>
                      ))}
                      <button
                        type="button"
                        onClick={() => setInvolvedEmployees([])}
                        style={{
                          padding: "6px 12px",
                          borderRadius: 999,
                          fontSize: 13,
                          fontWeight: 700,
                          border: "1.5px solid #FECACA",
                          background: "#FEF2F2",
                          color: "#DC2626",
                          cursor: "pointer",
                          fontFamily: "inherit",
                        }}
                      >
                        {t("Fjern alle")}
                      </button>
                    </div>
                  )}
                </div>

                <div>
                  <label style={dsLabelStyle}>{t("Vitner")}</label>
                  <input
                    type="text"
                    name="witnesses"
                    value={formik.values.witnesses}
                    onChange={formik.handleChange}
                    style={dsInputStyle}
                  />
                </div>

                <div>
                  <label style={dsLabelStyle}>{t("Andre involverte personer")}</label>
                  <input
                    type="text"
                    name="other_persons_involved"
                    value={formik.values.other_persons_involved}
                    onChange={formik.handleChange}
                    placeholder={t("Beskriv andre involverte personer...")}
                    style={dsInputStyle}
                  />
                </div>

                {/* ── Kategorisering ── */}
                <div style={{ ...dsSectionTitleStyle, marginTop: 8, ...spanFull(isWide) }}>{t("Kategorisering")}</div>

                {renderPickerTrigger(
                  t("Kategori"),
                  optionLabel(eventCategories, formik.values.event_category, t("Velg kategori")),
                  !!formik.values.event_category,
                  () => setCategoryPickerOpen(true)
                )}
                <StaticOptionPickerSheet
                  open={categoryPickerOpen}
                  onClose={() => setCategoryPickerOpen(false)}
                  title={t("Velg kategori")}
                  options={eventCategories}
                  selectedValue={formik.values.event_category}
                  onSelect={(opt) => formik.setFieldValue("event_category", opt.value)}
                  searchPlaceholder={t("Søk kategori…")}
                />

                <div>
                  <label style={dsLabelStyle}>{t("Umiddelbar årsak")}</label>
                  <input
                    type="text"
                    name="immediate_cause"
                    value={formik.values.immediate_cause}
                    onChange={formik.handleChange}
                    style={dsInputStyle}
                  />
                </div>

                <div>
                  <label style={dsLabelStyle}>{t("Underliggende årsak")}</label>
                  <input
                    type="text"
                    name="underlying_cause"
                    value={formik.values.underlying_cause}
                    onChange={formik.handleChange}
                    style={dsInputStyle}
                  />
                </div>

                <div style={spanFull(isWide)}>
                  <label style={dsLabelStyle}>{t("Detaljert kategorisering")}</label>
                  <textarea
                    rows={3}
                    name="category_details"
                    style={{ ...dsTextareaStyle, minHeight: 72 }}
                    placeholder={t("Beskriv kategoriseringen mer detaljert...")}
                    value={formik.values.category_details}
                    onChange={(e) => formik.setFieldValue("category_details", e.target.value)}
                  />
                </div>

                {/* ── Analyse og tiltak ── */}
                <div style={{ ...dsSectionTitleStyle, marginTop: 8, ...spanFull(isWide) }}>{t("Analyse og tiltak")}</div>

                <div
                  style={{
                    ...spanFull(isWide),
                    display: isWide ? "grid" : "flex",
                    gridTemplateColumns: isWide ? "1fr 1fr" : undefined,
                    flexDirection: "column",
                    gap: 10,
                  }}
                >
                  <DesignCheckbox
                    checked={formik.values.root_cause_analysis_required}
                    onChange={(e) => formik.setFieldValue("root_cause_analysis_required", e.target.checked)}
                    label={t("Årsakanalyse påkrevd")}
                  />
                  <DesignCheckbox
                    checked={formik.values.immediate_actions_required}
                    onChange={(e) => formik.setFieldValue("immediate_actions_required", e.target.checked)}
                    label={t("Strakstiltak påkrevd")}
                  />
                </div>

                {formik.values.root_cause_analysis_required && (
                  <div style={spanFull(isWide)}>
                    <label style={dsLabelStyle}>{t("Årsakanalyse")}</label>
                    <textarea
                      rows={4}
                      name="root_cause_analysis_details"
                      style={dsTextareaStyle}
                      placeholder={t("Beskriv årsakanalysen...")}
                      value={formik.values.root_cause_analysis_details}
                      onChange={(e) => formik.setFieldValue("root_cause_analysis_details", e.target.value)}
                    />
                  </div>
                )}

                {formik.values.immediate_actions_required && (
                  <div style={spanFull(isWide)}>
                    <label style={dsLabelStyle}>{t("Strakstiltak")}</label>
                    <textarea
                      rows={4}
                      name="immediate_actions_details"
                      style={dsTextareaStyle}
                      placeholder={t("Beskriv strakstiltakene...")}
                      value={formik.values.immediate_actions_details}
                      onChange={(e) => formik.setFieldValue("immediate_actions_details", e.target.value)}
                    />
                  </div>
                )}

                <div style={spanFull(isWide)}>
                  <label style={dsLabelStyle}>{t("Forslag til tiltak")}</label>
                  <textarea
                    rows={4}
                    name="suggested_actions"
                    style={dsTextareaStyle}
                    placeholder={t("Beskriv forslag til tiltak...")}
                    value={formik.values.suggested_actions}
                    onChange={(e) => formik.setFieldValue("suggested_actions", e.target.value)}
                  />
                </div>

                {/* ── Tilknyttede elementer ── */}
                {event && (
                  <>
                    <div style={{ ...dsSectionTitleStyle, marginTop: 8, ...spanFull(isWide) }}>{t("Tilknyttede elementer")}</div>
                    <div style={{ ...spanFull(isWide), display: "flex", flexDirection: "column", gap: 10 }}>
                      <CustomerTag customer={event?.customer} onRefresh={() => {}} />
                      <ProjectTag project={event?.project} onRefresh={() => {}} />
                      <EmployeeTag employee={event?.responsible_user} label={t("Ansvarlig")} onRefresh={() => {}} />
                      <ProjectFolderTag folder={event?.folder} label={t("Mappe")} onRefresh={() => {}} />
                    </div>
                  </>
                )}

                {/* ── Dokumenter ── */}
                <div style={{ ...dsSectionTitleStyle, marginTop: 8, ...spanFull(isWide) }}>{t("Dokumenter")}</div>
                <div style={{ ...spanFull(isWide), border: "1.5px solid #E2E8F0", borderRadius: 16, background: "#fff", padding: 12 }}>
                  {event?.id ? (
                    <DocumentManagerV6
                      module="hse_event"
                      moduleParam={event.id}
                      mode="dropzone"
                      allowCopy={false}
                      allowMove={false}
                      onDocumentsChange={(docs) =>
                        formik.setFieldValue("documents", Array.isArray(docs) ? docs : [])
                      }
                    />
                  ) : (
                    <p style={{ margin: 0, fontSize: 13, color: "#64748B" }}>
                      {t("hse.upload.save_first")}
                    </p>
                  )}
                </div>

                {/* Sticky bottom actions */}
                <div
                  style={{
                    ...spanFull(isWide),
                    display: "flex",
                    gap: 12,
                    position: "sticky",
                    bottom: 0,
                    zIndex: 5,
                    background: "#fff",
                    margin: "8px -16px -16px",
                    padding: "12px 16px",
                    borderTop: "1px solid #EEF1F5",
                  }}
                >
                  <button
                    type="button"
                    onClick={onCancel}
                    style={{
                      padding: "16px 22px",
                      borderRadius: 14,
                      border: "1.5px solid #E2E8F0",
                      background: "#fff",
                      color: "#475569",
                      fontWeight: 700,
                      fontSize: 16,
                      cursor: "pointer",
                      fontFamily: "inherit",
                      flex: "0 0 auto",
                    }}
                  >
                    {t("Avbryt")}
                  </button>
                  <button
                    type="submit"
                    disabled={!canSave}
                    style={{
                      flex: 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 8,
                      padding: 16,
                      borderRadius: 14,
                      border: "none",
                      fontFamily: "inherit",
                      fontSize: 16,
                      fontWeight: 800,
                      transition: "all .2s",
                      ...(canSave
                        ? {
                            color: "#fff",
                            background: ACCENT.grad,
                            boxShadow: `0 12px 24px -8px ${ACCENT.glow}.6)`,
                            cursor: "pointer",
                          }
                        : {
                            background: "#E2E8F0",
                            color: "#94A3B8",
                            cursor: "not-allowed",
                            boxShadow: "none",
                          }),
                    }}
                  >
                    {formik.isSubmitting || loading ? (
                      t("Lagrer...")
                    ) : (
                      <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M20 6L9 17l-5-5" />
                        </svg>
                        {event ? t("Oppdater") : t("Opprett")}
                      </span>
                    )}
                  </button>
                </div>
              </div>
            </Form>
          );
        }}
      </Formik>

      <MultiEmployeeSelector
        open={showMultiEmployeeSelector}
        onClose={() => setShowMultiEmployeeSelector(false)}
        selectedEmployees={involvedEmployees}
        onEmployeesSelected={handleInvolvedEmployeesSelected}
        label={t("Velg involverte personer")}
        placeholder={t("Søk i personer...")}
      />
    </div>
  );
};
